import { Injectable } from '@angular/core';
import { Paginated } from '@rms/sdk';
import { ExtensionType } from '@rms/team';
import {
  DialFirst,
  WidgetConfigurationHorizontalPosition,
  WidgetConfigurationPlacementMode,
  WidgetConfigurationSecurityMode,
  WidgetConfigurationVerticalPosition,
  WidgetStatus,
} from '@rms/widget';
import { delay, Observable, of } from 'rxjs';

import { WidgetCreateRequest, WidgetResponse, WidgetUpdateRequest } from '../models';

const MOCK_WIDGETS = [
  {
    id: 'mock-widget-1',
    name: 'Website Support',
    status: WidgetStatus.ENABLED,
    dial_first: DialFirst.CONTACT,
    outbound_cli_id: 'cli-1',
    created_at: '2025-01-15T10:00:00Z',
    updated_at: '2025-06-01T12:00:00Z',
    agent: { id: 'agent-1', name: 'Support Agent' },
    configuration: {
      styles: {
        accentColor: '#3547C8',
        backgroundColor: '#FFFFFF',
        placementMode: WidgetConfigurationPlacementMode.FLOATING,
        verticalPosition: WidgetConfigurationVerticalPosition.BOTTOM,
        horizontalPosition: WidgetConfigurationHorizontalPosition.RIGHT,
        verticalOffset: '20px',
        horizontalOffset: '20px',
      },
      logo: { source: null, width: 120, height: 40 },
      content: {
        title: { en: 'Need help? Call us!', it: 'Hai bisogno di aiuto? Chiamaci!' },
        button: { en: 'Call now', it: 'Chiama ora' },
        nameAvailable: true,
        emailAvailable: false,
      },
      security: {
        domainRestrictionMode: WidgetConfigurationSecurityMode.ALLOW_ALL,
        enabledCountryCodes: [],
      },
      webhook: null,
    },
  },
  {
    id: 'mock-widget-2',
    name: 'Sales Demo',
    status: WidgetStatus.DISABLED,
    dial_first: DialFirst.INTERNAL,
    outbound_cli_id: 'cli-2',
    created_at: '2025-03-10T08:30:00Z',
    updated_at: '2025-07-20T14:00:00Z',
    extension: { number: '202', type: ExtensionType.USER },
    configuration: {
      styles: {
        accentColor: '#B5372A',
        backgroundColor: '#FDECEA',
        placementMode: WidgetConfigurationPlacementMode.FLOATING,
        verticalPosition: WidgetConfigurationVerticalPosition.BOTTOM,
        horizontalPosition: WidgetConfigurationHorizontalPosition.LEFT,
        verticalOffset: '20px',
        horizontalOffset: '20px',
      },
      logo: { source: null, width: 100, height: 40 },
      content: {
        title: { en: 'Talk to Sales', it: 'Parla con le vendite' },
        button: { en: 'Book a demo', it: 'Prenota una demo' },
        nameAvailable: true,
        emailAvailable: true,
      },
      security: {
        domainRestrictionMode: WidgetConfigurationSecurityMode.LIMITED_DOMAINS,
        allowedDomains: ['romulus.live', 'staging.romulus.live'],
        enabledCountryCodes: ['IT', 'GB'],
      },
      webhook: null,
    },
  },
] as WidgetResponse[];

@Injectable()
export class WidgetsMockApiService {
  private readonly widgets = structuredClone(MOCK_WIDGETS);
  private nextId = this.widgets.length + 1;

  public getWidgets(page = 0, size = 20): Observable<Paginated<WidgetResponse>> {
    const content = this.widgets.slice(page * size, (page + 1) * size);

    return of({
      content,
      total_elements: this.widgets.length,
      total_pages: Math.ceil(this.widgets.length / size),
      size,
      number: page,
      number_of_elements: content.length,
      first: page === 0,
      last: (page + 1) * size >= this.widgets.length,
      empty: content.length === 0,
      sort: [],
      pageable: {
        offset: page * size,
        sort: [],
        page_number: page,
        page_size: size,
        paged: true,
        unpaged: false,
      },
    }).pipe(delay(300));
  }

  public getWidget(id: string): Observable<WidgetResponse> {
    const widget = this.widgets.find((w) => w.id === id) ?? this.widgets[0];

    return of({ ...widget }).pipe(delay(300));
  }

  public createWidget(_: WidgetCreateRequest): Observable<WidgetResponse> {
    return of(null as unknown as WidgetResponse).pipe(delay(300));
  }

  public updateWidget(id: string, request: WidgetUpdateRequest): Observable<WidgetResponse> {
    const index = this.widgets.findIndex((w) => w.id === id);
    const now = new Date().toISOString();

    if (index !== -1) {
      this.widgets[index] = { ...this.widgets[index], ...request, id, updated_at: now } as WidgetResponse;
    }

    return of({ ...(this.widgets[index] ?? this.widgets[0]) }).pipe(delay(300));
  }

  public deleteWidget(id: string): Observable<void> {
    const index = this.widgets.findIndex((w) => w.id === id);

    if (index !== -1) {
      this.widgets.splice(index, 1);
    }

    return of(undefined).pipe(delay(300));
  }
}
